<template>
  <div class="home-page" :style="{ backgroundImage: `url(${homeBg})` }">
    <div class="home-content">
      <img :src="homeTxt" alt="" class="home-intro" />

      <div class="book-list">
        <article
          v-for="book in books"
          :key="book.id"
          class="book-card"
          @click="handleBookClick(book)"
        >
          <img
            v-if="getBookCover(book)"
            :src="getBookCover(book)"
            :alt="book.title"
            class="book-cover-img"
          />
          <span v-if="!isBookUnlocked(book)" class="book-lock-badge">需绑定</span>
        </article>
      </div>
    </div>

    <div v-if="serialDialogVisible" class="serial-overlay" @click.self="closeSerialDialog">
      <div class="serial-dialog">
        <h2 class="serial-title">绑定序列号</h2>
        <p class="serial-desc">请输入该书本对应的序列号后开始学习</p>
        <input
          v-model="serialCode"
          class="serial-input"
          type="text"
          placeholder="例如 YS1-XXXX-XXXX-XXXX-XXXX-XXXX-XXXXXXX"
          @keyup.enter="submitSerial"
        />
        <p v-if="serialError" class="serial-error">{{ serialError }}</p>
        <div class="serial-actions">
          <button class="serial-btn light" @click="closeSerialDialog">取消</button>
          <button class="serial-btn" :disabled="serialSubmitting" @click="submitSerial">
            {{ serialSubmitting ? '绑定中...' : '确认绑定' }}
          </button>
        </div>
      </div>
    </div>

    <nav class="bottom-nav">
      <div class="nav-item active">
        <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l9 8v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V11l9-8z" fill="currentColor" />
        </svg>
        <span class="nav-text">首页</span>
      </div>
      <div class="nav-item" @click="goToMine">
        <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" fill="currentColor" />
        </svg>
        <span class="nav-text">我的</span>
      </div>
    </nav>
  </div>
</template>

<script>
import { getBooks, getUnlockedBooks, redeemSerial } from '@/api/book'
import { ensureWechatOpenid } from '@/utils/wechat-auth'
import homeTxt from '@/assets/home-txt.png'
import homeBg from '@/assets/home-bg.jpg'

export default {
  name: 'BookList',
  data() {
    return {
      homeTxt,
      homeBg,
      books: [],
      unlockedBookIds: [],
      loading: false,
      serialDialogVisible: false,
      selectedBookId: null,
      serialCode: '',
      serialError: '',
      serialSubmitting: false
    }
  },
  created() {
    this.initPage()
  },
  methods: {
    async initPage() {
      await this.fetchBooks()
      await this.fetchUnlockedBooks()
    },
    async fetchBooks() {
      this.loading = true
      try {
        const res = await getBooks()
        this.books = res.data || []
      } catch (error) {
        console.error('获取书本列表失败:', error)
        this.books = []
      } finally {
        this.loading = false
      }
    },
    async fetchUnlockedBooks() {
      try {
        await ensureWechatOpenid(this.$route)
        const res = await getUnlockedBooks()
        this.unlockedBookIds = (res.data && res.data.bookIds) || []
      } catch (error) {
        this.unlockedBookIds = []
      }
    },
    isBookUnlocked(book) {
      if (!book) return false
      if (book.requireSerial === false) return true
      return this.unlockedBookIds.indexOf(book.id) > -1
    },
    getBookCover(book) {
      return book.icon || book.cover || ''
    },
    async handleBookClick(book) {
      if (this.isBookUnlocked(book)) {
        this.goToBook(book.id)
        return
      }

      try {
        await ensureWechatOpenid(this.$route)
      } catch (error) {
        return
      }

      this.selectedBookId = book.id
      this.serialCode = ''
      this.serialError = ''
      this.serialDialogVisible = true
    },
    closeSerialDialog() {
      this.serialDialogVisible = false
      this.selectedBookId = null
      this.serialCode = ''
      this.serialError = ''
    },
    async submitSerial() {
      if (!this.selectedBookId || !this.serialCode.trim()) {
        this.serialError = '请输入序列号'
        return
      }

      this.serialSubmitting = true
      this.serialError = ''
      try {
        await ensureWechatOpenid(this.$route)
        await redeemSerial({
          code: this.serialCode.trim(),
          bookId: this.selectedBookId
        })
        if (this.unlockedBookIds.indexOf(this.selectedBookId) === -1) {
          this.unlockedBookIds = this.unlockedBookIds.concat(this.selectedBookId)
        }
        const bookId = this.selectedBookId
        this.closeSerialDialog()
        this.goToBook(bookId)
      } catch (error) {
        this.serialError = error.message || '绑定失败，请检查序列号'
      } finally {
        this.serialSubmitting = false
      }
    },
    goToBook(id) {
      this.$router.push(`/story-practice/${id}`)
    },
    goToMine() {
      this.$router.push('/records')
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  background-color: #e8f0c1;
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
}

.home-content {
  padding: 16px 16px 24px;
}

.home-intro {
  display: block;
  width: 100%;
  height: auto;
  margin-bottom: 20px;
}

.book-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.book-card {
  overflow: hidden;
  cursor: pointer;
}

.book-card:active {
  opacity: 0.92;
}

.book-cover-img {
  display: block;
  width: 100%;
  height: auto;
}

.book-lock-badge {
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
}

.serial-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 200;
}

.serial-dialog {
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: 24px;
  padding: 24px 20px;
}

.serial-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
}

.serial-desc {
  margin: 0 0 16px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.serial-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 16px;
  outline: none;
}

.serial-input:focus {
  border-color: #00b5ad;
}

.serial-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: #e74c3c;
}

.serial-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.serial-btn {
  flex: 1;
  border: none;
  background: #00b5ad;
  color: #fff;
  padding: 12px 16px;
  border-radius: 999px;
  font-size: 15px;
  cursor: pointer;
}

.serial-btn.light {
  background: #f2f2f2;
  color: #333;
}

.serial-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(56px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: #fff;
  display: flex;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 -1px 8px rgba(0, 0, 0, 0.06);
  z-index: 100;
}

.nav-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 96px;
  padding: 8px 16px;
  cursor: pointer;
  color: #999;
}

.nav-item.active {
  color: #00b5ad;
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-text {
  font-size: 14px;
  line-height: 1;
}
</style>



// WEBPACK FOOTER //
// src/views/BookList.vue